"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  motion,
  useScroll,
  useMotionValue,
  useMotionValueEvent,
  animate,
} from "motion/react";
import { Link } from "react-router-dom";

const FeatureHighlightMotion = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const sliderRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(Date.now());
  const lastXRef = useRef(0);

  // Track horizontal scroll progress with Framer Motion
  const { scrollXProgress } = useScroll({ container: sliderRef });
  const maskImage = useScrollOverflowMask(scrollXProgress);

  // Card data
  const cards = [
    {
      title: "Multi-ASIN Revenue Engine",
      description:
        "Build a diverse, performing products that consistently deliver massive returns",
    },
    {
      title: "Hands-Free Growth System",
      description:
        "Create systems that generate revenue while you focus on strategy and growth",
    },
    {
      title: "Niche Blueprint",
      description: "Become the undisputed market leader in your category",
    },
    {
      title: "Self-Scaling Sales",
      description:
        "Develop systems and processes that ensure consistent growth",
    },
    {
      title: "Category Leadership Insights",
      description:
        "Leverage analytics and insights to optimize every aspect of your business",
    },
  ];

  // Momentum scroll effect
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const animateScroll = () => {
      if (!isDragging && Math.abs(velocity) > 0.1) {
        slider.scrollLeft += velocity;
        setVelocity(velocity * 0.95); // Apply friction
        animationRef.current = requestAnimationFrame(animateScroll);
      }
    };

    if (!isDragging) {
      animationRef.current = requestAnimationFrame(animateScroll);
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isDragging, velocity]);

  // Mouse + Touch Handlers
  const startDrag = (xPosition) => {
    setIsDragging(true);
    setStartX(xPosition - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    setVelocity(0);
    lastXRef.current = xPosition;
    lastTimeRef.current = Date.now();
    sliderRef.current.style.userSelect = "none";
  };

  const dragMove = (xPosition) => {
    if (!isDragging) return;
    const x = xPosition - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    sliderRef.current.scrollLeft = scrollLeft - walk;

    const now = Date.now();
    const dt = now - lastTimeRef.current;
    const dx = xPosition - lastXRef.current;

    if (dt > 0) {
      setVelocity(-(dx / dt) * 10);
    }

    lastXRef.current = xPosition;
    lastTimeRef.current = now;
  };

  const stopDrag = () => {
    setIsDragging(false);
    sliderRef.current.style.userSelect = "auto";
  };

  // Handlers
  const handleMouseDown = (e) => startDrag(e.pageX);
  const handleMouseMove = (e) => dragMove(e.pageX);
  const handleTouchStart = (e) => startDrag(e.touches[0].pageX);
  const handleTouchMove = (e) => dragMove(e.touches[0].pageX);
  const handleMouseUp = stopDrag;
  const handleMouseLeave = stopDrag;
  const handleTouchEnd = stopDrag;

  return (
    <>
      <div className="feature-main-container">
        <div className="slider-section">
          {/* Scroll Progress Indicator */}
          {/* Cards Slider */}
          <motion.div
            className="cards-wrapper"
            ref={sliderRef}
            style={{ maskImage }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="cards-track">
              {cards.map((card, index) => (
                <div key={index} className="card">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
      <div className="hero-button-wrapper mt-4">
        <button className="btn hero-btn px-4 py-3 mb-5">
          <Link to={"/signin"} className="text-decoration-none">
            Get Show Me How to Scale
          </Link>
        </button>
      </div>
    </>
  );
};

// === Scroll Mask Hook (from your animation) ===
const left = `0%`;
const right = `100%`;
const leftInset = `20%`;
const rightInset = `80%`;
const transparent = "#0000";
const opaque = "#000";

export function useScrollOverflowMask(scrollXProgress) {
  const maskImage = useMotionValue(
    `linear-gradient(90deg, ${opaque}, ${opaque} ${left}, ${opaque} ${rightInset}, ${transparent})`
  );

  useMotionValueEvent(scrollXProgress, "change", (value) => {
    if (value === 0) {
      animate(
        maskImage,
        `linear-gradient(90deg, ${opaque}, ${opaque} ${left}, ${opaque} ${rightInset}, ${transparent})`
      );
    } else if (value === 1) {
      animate(
        maskImage,
        `linear-gradient(90deg, ${transparent}, ${opaque} ${leftInset}, ${opaque} ${right}, ${opaque})`
      );
    } else if (
      scrollXProgress.getPrevious() === 0 ||
      scrollXProgress.getPrevious() === 1
    ) {
      animate(
        maskImage,
        `linear-gradient(90deg, ${transparent}, ${opaque} ${leftInset}, ${opaque} ${rightInset}, ${transparent})`
      );
    }
  });

  return maskImage;
}

export default FeatureHighlightMotion;
